import type {
  SessionUser,
  SyncPayload,
  SyncResult,
  VehicleDto,
  BuildInfo,
} from "../../shared/types";
import { DISCLAIMER_TEXT, DISCLAIMER_VERSION } from "../../shared/types";

async function parseJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

export async function fetchBuildInfo(): Promise<BuildInfo> {
  const res = await fetch("/api/build-info", { credentials: "include" });
  return parseJson(res);
}

export async function login(email: string, password: string): Promise<SessionUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await parseJson<{ user: SessionUser }>(res);
  return data.user;
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
}

export async function fetchMe(): Promise<{
  user: SessionUser | null;
  disclaimer?: { version: string; text: string; accepted: boolean };
}> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  return parseJson(res);
}

export async function acceptDisclaimer(): Promise<void> {
  await parseJson(
    await fetch("/api/auth/disclaimer", {
      method: "POST",
      credentials: "include",
    }),
  );
}

export async function fetchVehicles(): Promise<VehicleDto[]> {
  const res = await fetch("/api/vehicles", { credentials: "include" });
  const data = await parseJson<{ vehicles: VehicleDto[] }>(res);
  return data.vehicles;
}

export async function pushSync(payload: SyncPayload): Promise<SyncResult> {
  const res = await fetch("/api/sync", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return parseJson(res);
}

export async function pullSync(): Promise<SyncResult> {
  const res = await fetch("/api/sync/pull", { credentials: "include" });
  return parseJson(res);
}

export async function uploadPhoto(
  id: string,
  blob: Blob,
  sha256: string,
): Promise<{ verificationStatus: string; serverConfirmed: boolean }> {
  const res = await fetch(`/api/photos/${id}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": blob.type || "application/octet-stream",
      "X-LogProof-SHA256": sha256,
      "X-LogProof-Size": String(blob.size),
    },
    body: blob,
  });
  return parseJson(res);
}

export async function retrieveWorkingDay(id: string): Promise<unknown> {
  const res = await fetch(`/api/retrieve/working-day/${id}`, {
    credentials: "include",
  });
  return parseJson(res);
}

export async function downloadPhotoBlob(id: string): Promise<Blob> {
  const res = await fetch(`/api/photos/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error("Could not download photo");
  return res.blob();
}

export { DISCLAIMER_TEXT, DISCLAIMER_VERSION };
